import { FAKE, REAL, registerDependency } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import type {
  VariantMatrixSearchResults,
  VariantMatrixSelectedFilter,
} from './typing/variant-matrix-typing.js'

export const VARIANT_MATRIX_API = Symbol('VARIANT_MATRIX_API')

export class VariantMatrixSearchRequest {
  endpointUrl: string
  method = 'GET' as const
  headers: HeadersInit = { Accept: 'application/json', 'Content-Type': 'application/json' }

  constructor(endpointUrl: string) {
    this.endpointUrl = endpointUrl
  }
}

export interface VariantMatrixAPI {
  getSearchResults(request: VariantMatrixSearchRequest): Promise<VariantMatrixSearchResults>
}

@registerDependency(FAKE, VARIANT_MATRIX_API)
export class FakeVariantMatrixAPI implements VariantMatrixAPI {
  requests: VariantMatrixSearchRequest[] = []
  searchResultsByUrl: Record<string, VariantMatrixSearchResults> = {}
  defaultSearchResults: VariantMatrixSearchResults | null = null
  private initialSearchUrl: string | null = null

  getSearchResults(request: VariantMatrixSearchRequest): Promise<VariantMatrixSearchResults> {
    this.requests.push(request)

    const cached = this.searchResultsByUrl[request.endpointUrl]
    if (cached) {
      return Promise.resolve(cached)
    }

    if (!this.initialSearchUrl) {
      this.initialSearchUrl = request.endpointUrl
    }

    const base = structuredClone(
      this.defaultSearchResults ?? {
        products: [],
        attributes: [],
        selected_filters: [],
        sort_modes: [],
        paginator: { has_next: false, next_page_api_link: '' },
        total_found: 0,
      },
    )

    return Promise.resolve(this.buildResultsForUrl(base, request.endpointUrl))
  }

  private buildResultsForUrl(
    base: VariantMatrixSearchResults,
    url: string,
  ): VariantMatrixSearchResults {
    const selectedValues = this.getSelectedFilterValues(url)

    if (!selectedValues.length) {
      return {
        ...base,
        attributes: base.attributes.map(attribute => ({
          ...attribute,
          options: (attribute.options ?? []).map(option => ({ ...option, selected: false })),
        })),
        selected_filters: [],
      }
    }

    const selected_filters: VariantMatrixSelectedFilter[] = []
    const attributes = base.attributes.map(attribute => ({
      ...attribute,
      options: (attribute.options ?? []).map(option => {
        const isSelected = selectedValues.some(value => this.optionMatchesValue(option.value, value))
        if (isSelected) {
          selected_filters.push({
            name: `${attribute.name} - ${option.name}`,
            api_link: this.initialSearchUrl ?? url,
          })
        }
        return { ...option, selected: isSelected }
      }),
    }))

    return { ...base, attributes, selected_filters }
  }

  private getSelectedFilterValues(url: string): string[] {
    try {
      const { searchParams } = new URL(url, 'http://localhost')
      return searchParams.getAll('v')
    } catch {
      const match = url.match(/[?&]v=([^&]+)/g)
      return match?.map(part => part.replace(/^[?&]v=/, '')) ?? []
    }
  }

  private optionMatchesValue(optionValue: string, filterValue: string): boolean {
    return optionValue.includes(`v=${filterValue}`) || optionValue.endsWith(filterValue)
  }
}

@registerDependency(REAL, VARIANT_MATRIX_API)
export class RealVariantMatrixAPI implements VariantMatrixAPI {
  async getSearchResults(request: VariantMatrixSearchRequest): Promise<VariantMatrixSearchResults> {
    const response = await fetch(request.endpointUrl, {
      method: request.method,
      headers: request.headers,
    })

    if (!response.ok) {
      throw new Error(`Variant matrix search failed with status ${response.status}`)
    }

    const contentType = response.headers.get('Content-Type') ?? ''
    if (!contentType.includes('application/json')) {
      throw new Error(`Variant matrix search returned '${contentType}' instead of JSON from ${request.endpointUrl}`)
    }

    return response.json()
  }
}
